import { hasPermission, getUserId } from "../../../../app/auth.js";
import { onStateChange, isDialogActive } from "../../../../app/activeStateManager.js";

let currentView = null;

const noPermissionRequired = ['toolTime', 'toolTimelogs', 'toolAdmin', 'toolSettings'];

function switchTool(toolName, sidebar) {
    const searchedTool = sidebar.querySelector(`#${toolName}`);
    if (!searchedTool) return;
    sidebar.querySelectorAll('.view.active').forEach(element => {
        if (element !== searchedTool) element.classList.remove('active');
    });
    searchedTool.classList.add('active');
}

export function switchSidebarTool(tool, viewOverride = null) {
    const sidebar = document.querySelector("app-sidebar").shadowRoot;
    currentView = viewOverride ?? tool.replace(/^tool/, '').toLowerCase();
    switchTool(tool, sidebar);
    applySidebarVisualState(sidebar);
}

const viewPermissionPrefix = {
    entries: "Entries",
    projects: "Projects",
    teams: "Teams",
    users: "Users",
    roles: "Roles",
    applications: "Applications",
    services: "Services",
    customers: "Customers",
};

function isOwnSelectedUser() {
    const loggedInUserId = getUserId();
    const selectedUserId = sessionStorage.getItem("selectedUser");
    return !selectedUserId || String(selectedUserId) === String(loggedInUserId);
}

// Customers and Users use app-panel-list instead of app-table, so there's no table to export yet.
const viewsWithoutExport = ['customers', 'users'];

const actionPermissionRules = {
    Create: (currentView, prefix) => {
        if (currentView === "timelogs") return true;
        if (currentView && currentView.startsWith("time")) return false;
        if (!prefix) return false;
        if (currentView === "entries") return isOwnSelectedUser() && hasPermission(`${prefix}.Create`);
        return hasPermission(`${prefix}.Create`);
    },
    Export: (currentView) => !viewsWithoutExport.includes(currentView),
};

export function checkToolPermission(action) {
    const rule = actionPermissionRules[action];
    if (!rule) return false;
    return rule(currentView, viewPermissionPrefix[currentView] ?? null);
}

const adminViews = ['projects', 'teams', 'roles', 'applications', 'services'];

const adminViewMeta = {
    teams:        { icon: 'fa-users-gear',     label: 'Teams' },
    roles:        { icon: 'fa-key',            label: 'Rollen' },
    projects:     { icon: 'fa-folder-plus',    label: 'Projekte' },
    applications: { icon: 'fa-window-restore', label: 'Applikationen' },
    services:     { icon: 'fa-wrench',         label: 'Services' },
};

function getAllowedAdminViews() {
    return adminViews.filter(view => hasPermission(`${viewPermissionPrefix[view]}.Read`));
}

export function getSingleAllowedAdminView() {
    const allowed = getAllowedAdminViews();
    return allowed.length === 1 ? allowed[0] : null;
}

function buildButtonConfig() {
    return [
        {
            id: 'toolCreate',
            allowed: () => checkToolPermission('Create'),
            active: () => ['createEntry', 'createProject', 'createTeam', 'createUser', 'createRole', 'createTimeLog'].some(isDialogActive),
            grayedWhenDisallowed: true,
        },
        {
            id: 'toolExport',
            allowed: () => checkToolPermission('Export'),
            active: () => isDialogActive('export'),
            grayedWhenDisallowed: true,
        },
        { id: 'toolEntries',    allowed: () => hasPermission('Entries.Read.Any') || hasPermission('Entries.Read.Own') },

        { id: 'toolCustomers',  allowed: () => hasPermission('Customers.Read') },
        { id: 'toolUsers',      allowed: () => hasPermission('Users.Read') },

        { id: 'toolAdmin',      active: () => adminViews.includes(currentView) },
        { id: 'popupTeams',        allowed: () => hasPermission('Teams.Read') },
        { id: 'popupRoles',        allowed: () => hasPermission('Roles.Read') },
        { id: 'popupProjects',     allowed: () => hasPermission('Projects.Read') },
        { id: 'popupApplications', allowed: () => hasPermission('Applications.Read') },
        { id: 'popupServices',     allowed: () => hasPermission('Services.Read') },

        { id: 'toolSettings',   active: () => isDialogActive('settings') }
    ];
}

export function applySidebarVisualState(sidebarInstance) {
    const shadow = sidebarInstance?.shadowRoot || document.querySelector("app-sidebar").shadowRoot;
    if (!shadow) return;

    for (const { id, allowed, active, grayedWhenDisallowed } of buildButtonConfig()) {
        const btn = shadow.getElementById(id);
        if (!btn) continue;

        if (allowed && !noPermissionRequired.includes(id)) {
            const isAllowed = allowed();
            if (grayedWhenDisallowed) {
                btn.classList.toggle('disabled', !isAllowed);
                btn.classList.remove('hidden');
            } else {
                btn.classList.toggle('hidden', !isAllowed);
                btn.classList.remove('disabled');
            }
        }

        if (active) {
            btn.classList.toggle('active', active());
        }
    }

    const toolAdmin = shadow.getElementById('toolAdmin');
    if (toolAdmin) {
        const allowedAdminViews = getAllowedAdminViews();
        toolAdmin.classList.toggle('hidden', allowedAdminViews.length === 0);
        const icon = toolAdmin.querySelector('i');
        if (allowedAdminViews.length === 1) {
            const meta = adminViewMeta[allowedAdminViews[0]];
            toolAdmin.setAttribute('tooltipp', meta.label);
            if (icon) icon.className = `fa-solid ${meta.icon} fa-lg`;
        } else {
            toolAdmin.setAttribute('tooltipp', 'Administration');
            if (icon) icon.className = 'fa-solid fa-screwdriver-wrench fa-lg';
        }
    }

    const adminSection = shadow.getElementById('adminSection');
    if (adminSection) {
        const hasAdminSectionPermission = hasPermission('Customers.Read') || hasPermission('Users.Read');
        adminSection.classList.toggle('hidden', !hasAdminSectionPermission);
        shadow.getElementById('adminSectionSeparator')?.classList.toggle('hidden', !hasAdminSectionPermission);
    }

    adminViews.forEach(view => {
        const id = `popup${view.charAt(0).toUpperCase() + view.slice(1)}`;
        shadow.getElementById(id)?.classList.toggle('active', currentView === view);
    });

    const hasTeamsOrRolesPermission = hasPermission('Teams.Read') || hasPermission('Roles.Read');
    shadow.getElementById('popupTeamsRolesSeparator')?.classList.toggle('hidden', !hasTeamsOrRolesPermission);

    // Icons start greyed out (see .tool's default color) until we've actually checked
    // permissions here - only then do the truly-allowed ones light up.
    shadow.querySelectorAll('.tool').forEach(tool => {
        tool.classList.toggle('resolved', !tool.classList.contains('disabled') && !tool.classList.contains('hidden'));
    });
}

export function attachStateListener(sidebarInstance) {
    if (!sidebarInstance) return;
    return onStateChange(() => applySidebarVisualState(sidebarInstance));
}